import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, Popup, LayersControl, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { farmApi, Farm } from '../lib/api';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

const getNdviColor = (ndvi: number | null) => {
  if (ndvi === null) return '#6b7280'; // Gray for no data
  if (ndvi < 0.2) return '#8B4513';
  if (ndvi < 0.4) return '#FFD700';
  if (ndvi < 0.6) return '#32CD32';
  return '#006400';
};

function FarmPolygon({ farm, showNdvi }: { farm: Farm; showNdvi: boolean }) {
  // Fetch real NDVI data for this farm
  const { data: farmStats } = useQuery({
    queryKey: ['farm-stats', farm.id],
    queryFn: async () => {
      try {
        return await farmApi.getStats(farm.id);
      } catch {
        return null;
      }
    },
  });

  const ndviValue = farmStats?.latestNdvi?.meanValue ?? null;

  // Guard against missing or empty boundary
  if (!farm.boundary || farm.boundary.length === 0) {
    return null;
  }

  const positions = farm.boundary.map((c) => [c.lat, c.lon] as [number, number]);

  return (
    <Polygon
      positions={positions}
      pathOptions={{
        color: '#1e40af',
        weight: 2,
        fillColor: showNdvi ? getNdviColor(ndviValue) : '#3b82f6',
        fillOpacity: showNdvi ? 0.7 : 0.3,
      }}
    >
      <Popup>
        <div className="min-w-[180px]">
          <h3 className="font-bold">{farm.name}</h3>
          <p className="text-sm text-gray-600">{farm.areaHectares?.toFixed(1)} ha</p>
          <p className="text-sm text-gray-600">{farm.cropType || 'Unknown crop'}</p>
          {showNdvi && (
            <p className="text-sm font-medium mt-1">
              NDVI: {ndviValue !== null ? ndviValue.toFixed(3) : 'N/A'}
            </p>
          )}
          <Link
            to={`/farms/${farm.id}`}
            className="mt-2 inline-block px-3 py-1 bg-green-600 text-white rounded text-sm"
          >
            View Details
          </Link>
        </div>
      </Popup>
    </Polygon>
  );
}

export default function MapView() {
  const [showNdvi, setShowNdvi] = useState(false);
  const [drawnCoords, setDrawnCoords] = useState<[number, number][] | null>(null);

  const { data: farms = [], isLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const handleDrawCreated = (e: any) => {
    const latLngs = e.layer.getLatLngs()[0];
    setDrawnCoords(latLngs.map((ll: any) => [ll.lat, ll.lng]));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="relative h-[calc(100vh-4rem)]">
      {/* Controls */}
      <div className="absolute top-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-4 max-w-xs">
        <h3 className="font-semibold mb-3">Map Controls</h3>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm">Show NDVI Overlay</span>
          <button
            onClick={() => setShowNdvi(!showNdvi)}
            className={`relative w-11 h-6 rounded-full transition-colors ${showNdvi ? 'bg-green-600' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${showNdvi ? 'left-6' : 'left-1'}`} />
          </button>
        </div>
        {showNdvi && (
          <div className="border-t pt-3">
            <p className="text-xs text-gray-600 mb-2">NDVI Scale</p>
            <div className="flex h-4 rounded overflow-hidden">
              <div className="flex-1" style={{ backgroundColor: '#8B4513' }} />
              <div className="flex-1" style={{ backgroundColor: '#FFD700' }} />
              <div className="flex-1" style={{ backgroundColor: '#32CD32' }} />
              <div className="flex-1" style={{ backgroundColor: '#006400' }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Bare</span>
              <span>Healthy</span>
            </div>
          </div>
        )}
      </div>

      {/* Drawn polygon info */}
      {drawnCoords && (
        <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-4">
          <h3 className="font-semibold mb-2">New Farm Boundary</h3>
          <p className="text-sm text-gray-600 mb-3">{drawnCoords.length} points drawn</p>
          <div className="flex space-x-2">
            <Link
              to="/farms/new"
              state={{ coordinates: drawnCoords }}
              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm"
            >
              Create Farm
            </Link>
            <button
              onClick={() => setDrawnCoords(null)}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Farm count */}
      <div className="absolute top-4 right-16 z-[1000] bg-white rounded-lg shadow-lg px-4 py-2">
        <p className="text-sm"><strong>{farms.length}</strong> farms</p>
      </div>

      {/* Map */}
      <MapContainer center={[44.38, -79.7]} zoom={10} className="h-full w-full">
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satellite">
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer>
        </LayersControl>
        
        <FeatureGroup>
          <EditControl
            position="topright"
            onCreated={handleDrawCreated}
            draw={{
              rectangle: false,
              circle: false,
              circlemarker: false,
              marker: false,
              polyline: false,
              polygon: { showArea: false, shapeOptions: { color: '#3b82f6', weight: 3, fillOpacity: 0.3 } },
            }}
          />
        </FeatureGroup>

        {farms.map((farm) => (
          <FarmPolygon key={farm.id} farm={farm} showNdvi={showNdvi} />
        ))}
      </MapContainer>
    </div>
  );
}
